import startCase from 'lodash/startCase';

import {
  Link3Outline,
  MeterOutline,
  NucleoAllTask,
  NucleoBookOpen,
  NucleoCameraRecordFilmMeetings,
  NucleoCrosshairsTarget,
  NucleoDialogue1On1ChatTalk,
  NucleoFeedbackDialogueThinkSpeech,
  NucleoHabitsLeaf,
  NucleoPraiseStar,
  NucleoProjects,
  NucleoSurvey,
  PuzzleOutline,
  SparkleOutline,
} from '@lyearn/icons';
import {
  RecommendedActionPersonaEnum,
  RecommendedActionsFilters,
  RecommendedActionTargetModuleEnum,
  RecommendedActionTypeEnum,
} from '@/types/schema';

import {
  InsightsActionModalTypeEnum,
  InsightsRecommendationItem,
  InsightsRecommendationSentimentTypeEnum,
} from './types';

export enum PillEnum {
  ALL = 'all',
  LEARN = 'learn',
  ALIGN_AND_ENGAGE = 'align_and_engage',
  PERFORM = 'perform',
}

export const MOCK_DATA = [
  {
    id: '6904e90f2323fbac4512bebc',
    type: 'CREATE_KNOWLEDGE_DOCUMENT',
    targetModule: 'LYEARN_COACH',
    status: 'SEEN',
    card: {
      title: 'Document Your Winning Strategy for Future Wins',
      subTitle: 'Capture today’s learnings to strengthen future wins.',
      avatar: null,
      reasons: [
        'Document win insights for faster replication.',
        'Create a reusable playbook from successful patterns.',
        'Preserve fresh details while they are actionable.',
        'Share knowledge to accelerate team performance and wins.',
      ],
      actions: [
        {
          type: 'BUTTON',
          label: 'Capture Win Insights',
          identifierId: null,
          path: '/coach/knowledge-documents/create',
          prefill: null,
          url: null,
          __typename: 'RecommendedActionCTA',
        },
      ],
      __typename: 'RecommendedActionCard',
    },
    __typename: 'RecommendedActionSchema',
  },
  {
    id: '6904e90f2323fbac4512bebb',
    type: 'NO_ACTION',
    targetModule: 'HUBSPOT',
    status: 'SEEN',
    card: {
      title: 'Boost deal velocity and win rate',
      subTitle: 'Closing deals quickly while improving win rate and momentum.',
      avatar: null,
      reasons: [
        'Faster cycles boost cash flow and capacity.',
        'Win rate improvement signals stronger qualification and follow-up.',
        'Sustained velocity maximizes revenue and builds trust.',
        'Momentum today enables scalable success and future growth.',
      ],
      actions: [],
      __typename: 'RecommendedActionCard',
    },
    __typename: 'RecommendedActionSchema',
  },
  {
    id: '6904e90f2323fbac4512bebd',
    type: 'EXTERNAL_LINK',
    targetModule: 'HUBSPOT',
    status: 'NEW',
    card: {
      title: 'Strengthen Pipeline Volume to Meet Targets',
      subTitle: 'Current pipeline at sixty-six deals, matching your benchmark.',
      avatar: null,
      reasons: [
        'Pipeline at 66 deals; risk remains without growth.',
        'Increase top-of-funnel activity to safeguard quotas.',
        'Improved coverage strengthens forecast confidence and outcomes.',
        'Proactive rebuild today ensures future revenue stability.',
      ],
      actions: [
        {
          type: 'URL',
          label: 'Open HubSpot',
          identifierId: null,
          path: null,
          prefill: null,
          url: 'https://app.hubspot.com',
          __typename: 'RecommendedActionCTA',
        },
      ],
      __typename: 'RecommendedActionCard',
    },
    __typename: 'RecommendedActionSchema',
  },
  {
    id: '6904e9132323fbac4512bebf',
    type: 'EXTERNAL_LINK',
    targetModule: 'HUBSPOT',
    status: 'NEW',
    card: {
      title: 'Increase Close Rate with Proven Win Strategies',
      subTitle: 'Current win rate underperforms; focus on reversals and growth.',
      avatar: null,
      reasons: [
        'Win rate sits below historical average.',
        'Analyze lost deals to uncover improvement opportunities.',
        'Address gaps to improve conversion and quota attainment.',
      ],
      actions: [
        {
          type: 'BUTTON',
          label: 'Review Lost Deals',
          identifierId: null,
          path: 'https://app.hubspot.com',
          prefill: null,
          url: null,
          __typename: 'RecommendedActionCTA',
        },
      ],
      __typename: 'RecommendedActionCard',
    },
    __typename: 'RecommendedActionSchema',
  },
] as unknown as InsightsRecommendationItem[];

interface TargetModuleInfo {
  title: string;
  Icon?: typeof Link3Outline;
}

const TARGET_MODULE_INFO_MAP: Partial<Record<RecommendedActionTargetModuleEnum, TargetModuleInfo>> =
  {
    [RecommendedActionTargetModuleEnum.ApolloIo]: {
      title: 'Apollo IO',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GithubRepo]: {
      title: 'GitHub',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.Gmail]: {
      title: 'Gmail',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GmailEmail]: {
      title: 'Gmail',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GmailSearch]: {
      title: 'Gmail',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GmailThread]: {
      title: 'Gmail',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GoogleCalendar]: {
      title: 'Google Calendar',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.GoogleMeet]: {
      title: 'Google Meet',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.Hubspot]: {
      title: 'Hubspot',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.HubspotContact]: {
      title: 'Hubspot',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.HubspotDeal]: {
      title: 'Hubspot',
      Icon: Link3Outline,
    },
    [RecommendedActionTargetModuleEnum.LyearnCoach]: {
      title: 'Coach',
      Icon: SparkleOutline,
    },
    [RecommendedActionTargetModuleEnum.LyearnFeedback]: {
      title: 'Feedback',
      Icon: NucleoFeedbackDialogueThinkSpeech,
    },
    [RecommendedActionTargetModuleEnum.LyearnHabits]: {
      title: 'Habits',
      Icon: NucleoHabitsLeaf,
    },
    [RecommendedActionTargetModuleEnum.LyearnIntegration]: {
      title: 'Integration',
      Icon: PuzzleOutline,
    },
    [RecommendedActionTargetModuleEnum.LyearnLearning]: {
      title: 'Learning',
      Icon: NucleoBookOpen,
    },
    [RecommendedActionTargetModuleEnum.LyearnMeetings]: {
      title: 'Meetings',
      Icon: NucleoCameraRecordFilmMeetings,
    },
    [RecommendedActionTargetModuleEnum.LyearnMgsa]: {
      title: 'MGSA',
      Icon: MeterOutline,
    },
    [RecommendedActionTargetModuleEnum.LyearnOkrs]: {
      title: 'OKRs',
      Icon: NucleoCrosshairsTarget,
    },
    [RecommendedActionTargetModuleEnum.LyearnOneOnOnes]: {
      title: '1:1',
      Icon: NucleoDialogue1On1ChatTalk,
    },
    [RecommendedActionTargetModuleEnum.LyearnPraise]: {
      title: 'Praise',
      Icon: NucleoPraiseStar,
    },
    [RecommendedActionTargetModuleEnum.LyearnProjects]: {
      title: 'Projects',
      Icon: NucleoProjects,
    },
    [RecommendedActionTargetModuleEnum.LyearnSurveys]: {
      title: 'Surveys',
      Icon: NucleoSurvey,
    },
    [RecommendedActionTargetModuleEnum.LyearnTasks]: {
      title: 'Tasks',
      Icon: NucleoAllTask,
    },
    [RecommendedActionTargetModuleEnum.Slack]: {
      title: 'Slack',
      Icon: Link3Outline,
    },
  };

const SENTIMENT_COLOR: Partial<Record<InsightsRecommendationSentimentTypeEnum, string>> = {
  [InsightsRecommendationSentimentTypeEnum.POSITIVE]: 'text-icon-success',
  [InsightsRecommendationSentimentTypeEnum.NEGATIVE]: 'text-icon-error',
  [InsightsRecommendationSentimentTypeEnum.NEUTRAL]: 'text-text-secondary',
};

export const getRecommendationIcon = (recommendation: InsightsRecommendationItem) => {
  return TARGET_MODULE_INFO_MAP[recommendation.targetModule]?.Icon ?? Link3Outline;
};

export const getModalTypeByActionType = (type: RecommendedActionTypeEnum) => {
  switch (type) {
    // case RecommendedActionTypeEnum.CREATE_OKR:
    case RecommendedActionTypeEnum.CheckinOkr:
      return InsightsActionModalTypeEnum.CHECKIN_OKR;

    case RecommendedActionTypeEnum.SetupOneOnOne:
      return InsightsActionModalTypeEnum.SETUP_ONE_ON_ONE;

    case RecommendedActionTypeEnum.GiveFeedback:
      return InsightsActionModalTypeEnum.GIVE_FEEDBACK;

    // case RecommendedActionTypeEnum.REQUEST_FEEDBACK:
    //   return InsightsActionModalTypeEnum.REQUEST_FEEDBACK;

    // case RecommendedActionTypeEnum.CREATE_FEEDBACK:
    //   return InsightsActionModalTypeEnum.CREATE_FEEDBACK;

    case RecommendedActionTypeEnum.EnrollInCourse:
    case RecommendedActionTypeEnum.AssignContent:
      // todo- handle separately
      return null;

    case RecommendedActionTypeEnum.CreateHabit:
      return InsightsActionModalTypeEnum.CREATE_HABIT;

    case RecommendedActionTypeEnum.CreateMeeting:
      return InsightsActionModalTypeEnum.SCHEDULE_MEETING;

    // case RecommendedActionTypeEnum.CREATE_FEEDBACK:
    //   return InsightsActionModalTypeEnum.CREATE_FEEDBACK;

    // case RecommendedActionTypeEnum.CREATE_KNOWLEDGE_DOCUMENT:
    //   return InsightsActionModalTypeEnum.CREATE_KNOWLEDGE_DOCUMENT;

    case RecommendedActionTypeEnum.GivePraise:
      return InsightsActionModalTypeEnum.GIVE_PRAISE;

    // case RecommendedActionTypeEnum.CREATE_SURVEY:
    //   return InsightsActionModalTypeEnum.CREATE_SURVEY;

    // case RecommendedActionTypeEnum.SETUP_PROJECT:
    //   return InsightsActionModalTypeEnum.SETUP_PROJECT;

    // case RecommendedActionTypeEnum.CREATE_TASK:
    //   return InsightsActionModalTypeEnum.CREATE_TASK;

    // case RecommendedActionTypeEnum.COMPLETE_TASK:
    //   return InsightsActionModalTypeEnum.COMPLETE_TASK;

    // case RecommendedActionTypeEnum.SETUP_INTEGRATION:
    //   return InsightsActionModalTypeEnum.SETUP_INTEGRATION;

    default:
      return null;
  }
};

export function getSelfAiRecommendationsFilters({
  currentUserId,
}: {
  currentUserId: string;
}): RecommendedActionsFilters {
  return {
    targetEntityIds: [currentUserId],
    isActive: true,
  };
}

export function getMyTeamAiRecommendationsFilters({
  currentUserId,
}: {
  currentUserId: string;
}): RecommendedActionsFilters {
  return {
    targetEntityIds: [currentUserId],
    isActive: true,
    personas: [RecommendedActionPersonaEnum.Manager],
  };
}

export function getReporteeAiRecommendationsFilters({
  currentUserId,
  reporteeId,
}: {
  currentUserId: string;
  reporteeId: string;
}): RecommendedActionsFilters {
  return {
    targetEntityIds: [currentUserId],
    isActive: true,
    affectedEntityIds: [reporteeId],
  };
}

// export const getIconBorderColor = (targetModule: RecommendedActionTargetModuleEnum) => {
//   switch (targetModule) {
//     case RecommendedActionTargetModuleEnum.LyearnOkrs:
//     case RecommendedActionTargetModuleEnum.LyearnMeetings:
//       return 'border-border-warning';
//     case RecommendedActionTargetModuleEnum.LyearnPraise:
//     case RecommendedActionTargetModuleEnum.LyearnOneOnOnes:
//     case RecommendedActionTargetModuleEnum.LyearnSurveys:
//     case RecommendedActionTargetModuleEnum.LyearnFeedback:
//       return 'border-border-pink';
//     case RecommendedActionTargetModuleEnum.LyearnHabits:
//     case RecommendedActionTargetModuleEnum.LyearnTasks:
//     case RecommendedActionTargetModuleEnum.LyearnProjects:
//       return 'border-border-success';
//     case RecommendedActionTargetModuleEnum.LyearnLearning:
//       return 'border-border-cyan';
//     case RecommendedActionTargetModuleEnum.LyearnCoach:
//     case RecommendedActionTargetModuleEnum.LyearnMgsa:
//       return 'border-border-purple';
//     default:
//       return 'border-border';
//   }
// };

export const getActionTargetTitle = (targetModule: RecommendedActionTargetModuleEnum) => {
  return TARGET_MODULE_INFO_MAP[targetModule]?.title ?? startCase(targetModule.toLowerCase());
};

export function getModulesFromSelectedPill(
  selectedPills: PillEnum,
): RecommendedActionTargetModuleEnum[] {
  switch (selectedPills) {
    case PillEnum.ALIGN_AND_ENGAGE:
      return [
        RecommendedActionTargetModuleEnum.LyearnOkrs,
        RecommendedActionTargetModuleEnum.LyearnSurveys,
        RecommendedActionTargetModuleEnum.LyearnMeetings,
        RecommendedActionTargetModuleEnum.LyearnOneOnOnes,
        RecommendedActionTargetModuleEnum.LyearnFeedback,
        RecommendedActionTargetModuleEnum.LyearnPraise,
      ];
    case PillEnum.PERFORM:
      return [
        RecommendedActionTargetModuleEnum.LyearnHabits,
        RecommendedActionTargetModuleEnum.LyearnTasks,
        RecommendedActionTargetModuleEnum.LyearnProjects,
        RecommendedActionTargetModuleEnum.LyearnHabits,
      ];
    case PillEnum.LEARN:
      return [
        RecommendedActionTargetModuleEnum.LyearnLearning,
        RecommendedActionTargetModuleEnum.LyearnCoach,
        RecommendedActionTargetModuleEnum.LyearnMgsa,
      ];

    default:
      return [];
  }
}
